import { useState, useEffect, useMemo, useRef } from "react"
import Loader from "../../UI/Loader/Loader"
import { useContentfulContext } from "../../../contexts/ContentfulContext"
import { useUserContext } from "../../../contexts/UserContext"
import useComponentVisible from "../../UI/ComponentVisible"
import SpinLoader from "../../UI/Loader/SpinLoader"
import Dropdown from "../../UI/Dropdown/Dropdown"
import AgGridWrapper from "../../UI/AgGrid"
import { PrimaryButton, ErrorButton } from "../../UI";
import {
  createEntityMappings,
  getAllActivityData,
  getDataFromCsvURL,
  getEntityMappings,
  saveEntityData,
} from "../../../utils/APIs"
import Papa from "papaparse"
import { CheckCircle, XCircle, EllipsisVertical } from "lucide-react"
import { toast } from "react-toastify"
import recommendationEntitySample from "../../../assets/recommendation-entity-sample.csv"
import PrimaryModal from "../../UI/Modal/PrimaryModal"
import MappingModal from "./MappingModal"
import { xmlToJsonSimplified } from "../../../utils/helper"

const CSV_DATA_STATUS = {
  error: "",
  success: "",
}

const ENTITY_TYPES = {
  CSV_UPLOAD: "csv-upload",
  CSV_URL: "csv-url",
  XML_URL: "xml-url",
  CSV_UPLOAD_WITH_CUSTOM_TEMPLATE: "csv-upload-with-custom-template",
}

const ManageRecommendationAddEntity = ({ handleCloseAddEntity, environments }) => {
  const { content } = useContentfulContext()
  const { userData } = useUserContext()
  const fileInputRef = useRef("")
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 })
  const [loading, setLoading] = useState(false)
  const [validateCsvLoading, setValidateCsvLoading] = useState(false)
  const [selectedEnvironment, setSelectedEnvironment] = useState(null)
  const [selectedEntity, setSelectedEntity] = useState(null)
  const [error, setError] = useState("")
  const [csvValidation, setCsvValidation] = useState(CSV_DATA_STATUS)
  const [rowData, setRowData] = useState([])
  const [selectedRowData, setSelectedRowData] = useState({})
  const [deleteIds, setDeleteIds] = useState([])
  const [errors, setErrors] = useState({})
  const [saveEntityMessage, setSaveEntityMessage] = useState({text : "", type : "error"})

  // URL data source states
  const [url, setUrl] = useState("")
  const [jsonData, setJsonData] = useState(null)
  const [targetPath, setTargetPath] = useState("")
  const [showTargetPathInput, setShowTargetPathInput] = useState(false)
  const [availableFields, setAvailableFields] = useState([])
  const [stringFields, setStringFields] = useState([])

  // mappings states
  const [showFieldMappingSection, setShowFieldMappingSection] = useState(false)
  const [mappings, setMappings] = useState([])
  const [selectedMapping, setSelectedMapping] = useState(null)
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false)
  const [editingMapping, setEditingMapping] = useState(null)

  const entityOptions = [
    { id: ENTITY_TYPES.CSV_UPLOAD, name: content.MANAGE_RECOMMENDATION.entities.csv_upload },
    { id: ENTITY_TYPES.CSV_URL, name: content.MANAGE_RECOMMENDATION.entities.csv_url },
    { id: ENTITY_TYPES.XML_URL, name: content.MANAGE_RECOMMENDATION.entities.xml_url },
    { id: ENTITY_TYPES.CSV_UPLOAD_WITH_CUSTOM_TEMPLATE, name: content.MANAGE_RECOMMENDATION.entities.csv_upload_with_custom_template },
  ]

  // Extract string fields from the first object in availableFields
  const extractStringFields = (data) => {
    if (!data || data.length === 0) return []

    const firstObject = data[0]
    const fields = []

    Object.keys(firstObject).forEach((key) => {
      const value = firstObject[key]
      // Check if the value is a string (simple value)
      if (typeof value === "string") {
        fields.push(key)
      }
    })

    return fields
  }

  const fetchMappings = async () => {
    try {
      setLoading(true)
      const result = await getEntityMappings(userData?.token, userData?.selectedOrganization?.org_token)
      if (result.status && Array.isArray(result.data)) {
        const filteredMappings = result.data
          .filter((mapping) => mapping.ingestion_method === selectedEntity?.id)
          .map((mapping) => ({
            ...mapping,
            id: mapping._id,
            name: mapping.name,
          }))

        setMappings(filteredMappings)
      }
      setError("")
    } catch (error) {
      console.error("Error fetching mappings:", error)
      setError("Failed to load mappings")
    } finally {
      setLoading(false)
    }
  }

  const handleCreateMapping = (name, mappings) => {
    const createMapping = async () => {
      try {
        setLoading(true)
        const data = {
          name,
          mappings,
          ingestion_method :selectedEntity?.id
        }

        const result = await createEntityMappings(userData?.token, userData?.selectedOrganization?.org_token, data)

        // Refresh mappings list
        if (result.status) {
          await fetchMappings()
          setIsMappingModalOpen(false)
        }
      } catch (error) {
        console.error("Error creating mapping:", error)
        setError("Failed to create mapping")
      } finally {
        setLoading(false)
      }
    }

    createMapping()
  }

  const handleMappingSelect = (mapping) => {
    setSelectedMapping(mapping);

    const fullMapping = mappings.find((m) => m.id === mapping.id);

    if (!fullMapping || !fullMapping.mappings) return;

    let processedData = []

    if (selectedEntity?.id === ENTITY_TYPES.CSV_UPLOAD ||
      selectedEntity?.id === ENTITY_TYPES.CSV_UPLOAD_WITH_CUSTOM_TEMPLATE) {
      const inputToOutputMap = {};
      fullMapping.mappings.forEach(({ input_field, output_field }) => {
        inputToOutputMap[input_field] = output_field;
      });
      processedData = availableFields.map((item) => {
        const entity = {};

        // For each field in the item, either map it (if mapping exists) or keep it as is
        Object.keys(item).forEach((inputField) => {
          const outputField = inputToOutputMap[inputField] || inputField;

          // Ensure only fields from the actual data are included
          entity[`entity.${outputField}`] = item[inputField] ?? "";
        });

        return entity;
      });
    } else {
      // 1. Build fieldMapping: output_field → input_field
      const dynamicFieldMapping = {};
      console.log("fullMapping.mappings", fullMapping.mappings)
      fullMapping.mappings.forEach(({ input_field, output_field }) => {
        dynamicFieldMapping[output_field] = input_field;
      });

      // 2. Transform availableFields based on mapping
      processedData = availableFields.map((item) => {
        const entity = {};

        Object.entries(dynamicFieldMapping).forEach(([outputField, inputField]) => {
          entity[`entity.${outputField}`] = item[inputField] || "";
        });

        return entity;
      });
    }
    console.log("processedData", processedData)
    setRowData(processedData);
    checkValidation(processedData);
    setCsvValidation({
      error: "",
      success: `Successfully processed ${processedData.length} entities. Review the data below.`,
    });
  };

  const handleChooseEnvironment = (val) => {
    setSelectedEnvironment(val)
    setError("")
  }

  const handleChooseEntity = (val) => {
    setSelectedEntity(val)
    setError("")
    // Reset states when changing entity type
    setUrl("")
    setJsonData(null)
    setTargetPath("")
    setShowTargetPathInput(false)
    setAvailableFields([])
    setShowFieldMappingSection(false)
    setRowData([])
    setSelectedMapping(null)
    setCsvValidation(CSV_DATA_STATUS)
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  const downloadCsv = async () => {
    if (!selectedEntity) return

    try {
      const response = await fetch(recommendationEntitySample)
      const csvText = await response.text()

      const filename = `recommendation-entity-sample.csv`
      const link = document.createElement("a")
      const blob = new Blob([csvText], { type: "text/csv" })
      const url = URL.createObjectURL(blob)

      link.setAttribute("href", url)
      link.setAttribute("download", filename)
      link.click()

      URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Error downloading CSV:", error)
      toast.error(content?.MANAGE_RECOMMENDATION?.errors?.download_failed || "Failed to download sample file")
    }
  }

  const cleanHeader = (header) => {
    if (!header) return ""

    let cleanedHeader = header.toString().trim().replace(/"/g, "")

    if (cleanedHeader.startsWith("## RECS")) {
      cleanedHeader = cleanedHeader.replace("## RECS", "").trim()
    }

    cleanedHeader = cleanedHeader.replace(/^#+\s*/, "").trim()

    return cleanedHeader
  }

  const handleSelectCSV = (event) => {
    setRowData([])
    setEditingMapping(null)
    setSelectedMapping(null)
    const file = event.target.files[0]
    setCsvValidation(CSV_DATA_STATUS)
    if (!file) {
      setError(content?.MANAGE_RECOMMENDATION?.errors?.file_required || "File is required")
      return
    }

    setValidateCsvLoading(true)

    Papa.parse(file, {
      header: false,
      dynamicTyping: false,
      encoding: "utf-8",
      skipEmptyLines: false,
      complete: (results) => {
        try {
          const rowHeaderIndex = selectedEntity.id === ENTITY_TYPES.CSV_UPLOAD_WITH_CUSTOM_TEMPLATE ? 0 : 6

          if (results.data.length < (rowHeaderIndex+1)) {
            setError(`CSV file must have at least ${rowHeaderIndex+1} rows`)
            setValidateCsvLoading(false)
            return
          }

          const rawHeaders = results.data[rowHeaderIndex] || []
          const cleanedHeaders = rawHeaders.map((h) => cleanHeader(h)).filter((h) => h !== "")
          
          const dataRows = []
          for (let i = rowHeaderIndex+1; i < results.data.length; i++) {
            const row = results.data[i]
            if (!row || row.length === 0) continue

            const rowObject = {}
            let hasData = false

            cleanedHeaders.forEach((header, index) => {
              if (header && row[index] !== undefined) {
                const value = row[index] ? row[index].toString().trim() : ""
                rowObject[header] = value
                if (value !== "") hasData = true
              }
            })

            if (hasData) {
              dataRows.push(rowObject)
            }
          }

          if (dataRows.length === 0) {
            setError(content?.MANAGE_RECOMMENDATION?.errors?.no_data_found || "No valid data found in CSV file")
            setValidateCsvLoading(false)
            return
          }

          setError("")
          if(selectedEntity.id == ENTITY_TYPES.CSV_UPLOAD) {
            checkValidation(dataRows)
          }

          // Add this condition to show field mapping for custom template
          if (selectedEntity?.id === ENTITY_TYPES.CSV_UPLOAD_WITH_CUSTOM_TEMPLATE) {
            // const normalizedDataRows = dataRows.map(row => {
            //   const normalizedRow = {};
            //   Object.keys(row).forEach((key) => {
            //     const normalizedKey = key.startsWith("entity.") ? key.replace("entity.", "") : key;
            //     normalizedRow[normalizedKey] = row[key];
            //   });
            //   return normalizedRow;
            // });

            console.log("dataRows", dataRows)
            const fields = extractStringFields(dataRows);
            console.log("string fields", fields);

            setStringFields(fields);
            setAvailableFields(dataRows);
            setShowFieldMappingSection(true);
            fetchMappings();
          }else{
            setRowData(dataRows)
          }

          setValidateCsvLoading(false)
        } catch (error) {
          console.error("Error processing CSV:", error)
          setError(content?.MANAGE_RECOMMENDATION?.errors?.processing_failed || "Failed to process CSV file")
          setValidateCsvLoading(false)
        }
      },
      error: (error) => {
        console.error("CSV parsing error:", error)
        setError(content?.MANAGE_RECOMMENDATION?.errors?.parsing_failed || "Failed to parse CSV file")
        setValidateCsvLoading(false)
      },
    })
  }

  const checkValidation = (data) => {
    const newErrors = {}
    const entityNames = data.map((x) => x["name"] || x["entity.name"] || "")
    const entityIds = data.map((x) => x["id"] || x["entity.id"] || "")
    let isValid = true

    data.forEach((row, rowIndex) => {
      if (!newErrors[rowIndex]) newErrors[rowIndex] = {}

      const entityId = row["id"] || row["entity.id"] || ""
      const entityName = row["name"] || row["entity.name"] || ""

      if (!entityId) {
        const idField = row["id"] !== undefined ? "id" : "entity.id"
        newErrors[rowIndex][idField] =
          content?.MANAGE_RECOMMENDATION?.errors?.entity_id_required || "Entity ID is required."
        isValid = false
      }

      if (!entityName) {
        const nameField = row["name"] !== undefined ? "name" : "entity.name"
        newErrors[rowIndex][nameField] =
          content?.MANAGE_RECOMMENDATION?.errors?.entity_name_required || "Entity name is required."
        isValid = false
      }

      // currently not checking duplicate names
      // const nameCount = entityNames.filter((x) => x === entityName).length
      // if (nameCount > 1 && entityName) {
      //   const nameField = row["name"] !== undefined ? "name" : "entity.name"
      //   newErrors[rowIndex][nameField] =
      //     `${content?.MANAGE_RECOMMENDATION?.errors?.duplicate_entity_name || "Duplicate Entity name"}: ${entityName}`
      //   isValid = false
      // }

      const idCount = entityIds.filter((x) => x === entityId).length
      if (idCount > 1 && entityId) {
        const idField = row["id"] !== undefined ? "id" : "entity.id"
        newErrors[rowIndex][idField] =
          `${content?.MANAGE_RECOMMENDATION?.errors?.duplicate_entity_id || "Duplicate Entity ID"}: ${entityId}`
        isValid = false
      }
    })

    if (isValid) {
      setCsvValidation({
        error: "",
        success: content?.MANAGE_RECOMMENDATION?.messages?.validation_success || "CSV validation successful",
      })
    } else {
      setCsvValidation({
        error:
          content?.MANAGE_RECOMMENDATION?.errors?.validation_failed ||
          "CSV validation failed. Please check the errors.",
        success: "",
      })
    }

    setErrors(newErrors)
    return newErrors
  }

  const checkCellValidation = (rowIndex, colId, value) => {
    const temp = JSON.parse(JSON.stringify(errors))

    if (!temp[rowIndex]) temp[rowIndex] = {}
    const updateMessage = () =>
      setCsvValidation({
        error:
          "Some of the data is invalid. Please refer to the 'Status' column to identify the row with invalid data.",
        success: "",
      })

    if (colId == "entity.id") {
      if (!value) {
        temp[rowIndex]["entity.id"] = `ID is required.`
        setErrors(temp)
        updateMessage()
        return
      }

      temp[rowIndex]["entity.id"] = ""
      delete temp[rowIndex]["entity.id"]
      setErrors(temp)
    }
    if (colId == "entity.name") {
      if (!value) {
        temp[rowIndex]["entity.name"] = `Name is required.`
        setErrors(temp)
        updateMessage()
        return
      }

      temp[rowIndex]["entity.name"] = ""
      delete temp[rowIndex]["entity.name"]
      setErrors(temp)
    }
    // if (colId == "entity.categoryId") {
    //   if (!value) {
    //     temp[rowIndex]["entity.categoryId"] = `Category ID is required.`
    //     setErrors(temp)
    //     updateMessage()
    //     return
    //   }

    //   temp[rowIndex]["entity.categoryId"] = ""
    //   delete temp[rowIndex]["entity.categoryId"]
    //   setErrors(temp)
    // }
    setCommonErrorMessage(temp)
  }

  function setCommonErrorMessage(errs) {
    // Check if any key in the error object has an error message
    const hasError = Object.values(errs ?? errors).some((errorDetails) => Object.keys(errorDetails).length > 0)
    if (!hasError)
      setCsvValidation({ error: "", success: content.ADD_EXPERIENCE_MODAL.add_experience_modal.csv_validation_success })
  }

  const handleSubmit = async () => {
    try {
      setError("")
      setSaveEntityMessage({text:"", type : ""})

      const errs = checkValidation(rowData)
      if (Object.values(errs).some((errObj) => Object.keys(errObj).length > 0)) {
        return
      }

      if (!selectedEnvironment) {
        setError(content?.MANAGE_RECOMMENDATION?.errors?.environment_required || "Please select an environment")
        return
      }

      const entities = rowData.map((row) => {
        const entity = {
          name: row["name"] || row["entity.name"] || "",
          id: row["id"] || row["entity.id"] || "",
          environment: selectedEnvironment.id,
          categories: [],
          attributes: {},
        }
        // output_field need to be exactly categoryId to add data in categories
        const categoriesField = row["entity.categoryId"] || row["categoryId"] || ""
        if (categoriesField) {
          entity.categories = categoriesField
            .split(",")
            .map((cat) => cat.trim())
            .filter((cat) => cat)
        }

        Object.keys(row).forEach((key) => {
          if (
            key.startsWith("entity.") &&
            key !== "entity.categories" &&
            key !== "entity.categoryId" &&
            key !== "entity.name" &&
            key !== "entity.id"
          ) {
            if (key.includes("entity")) {
              const attributeName = key.replace("entity.", "")
              entity.attributes[attributeName] = row[key] || ""
            }
          }
        })

        return entity
      })

      setLoading(true)
      setCsvValidation("")
      console.log("entities", entities)
      const result = await saveEntityData(
        userData?.token,
        userData?.selectedOrganization?.org_token,
        userData?.defaultAdobeIO?.tenant_name,
        {
          entities,
        },
      )

      if (result.status) {

        setSaveEntityMessage({text: result.message, type : "success"})
        // setRowData([])
        // setSelectedEntity(null)
        // if (fileInputRef.current) {
        //   fileInputRef.current.value = ""
        // }
      }
      // handleCloseAddEntity()
    } catch (error) {
      console.log("error handleSubmit: ", error)
      const errMsg = error?.response?.data?.message || error?.message || content?.MANAGE_RECOMMENDATION?.errors?.save_failed || "Something went wrong"
      // setError(errMsg || content?.COMMON_MESSAGE.something_wrong_msg)
      setSaveEntityMessage({text:errMsg, type : "error"})
    } finally {
      setLoading(false)
    }
  }

  const handleOpenThreeDotMenu = (e, params) => {
    e.preventDefault()
    e.stopPropagation()

    const buttonRect = e.currentTarget.getBoundingClientRect()
    const containerRect = e.currentTarget.closest(".p-6").getBoundingClientRect()

    const menuPosition = {
      top: buttonRect.bottom - containerRect.top,
      left: buttonRect.left - containerRect.left - 192,
    }

    setMenuPosition(menuPosition)
    setSelectedRowData(params)
    setIsComponentVisible(!isComponentVisible)
  }

  const handleAddDuplicateRow = () => {
    const originalName = selectedRowData.data["name"] || selectedRowData.data["entity.name"] || ""
    const originalId = selectedRowData.data["id"] || selectedRowData.data["entity.id"] || ""

    const newRow = {
      ...rowData[selectedRowData.rowIndex],
    }

    if (newRow["name"] !== undefined) {
      newRow["name"] = `Copy ${originalName}`
    }
    if (newRow["entity.name"] !== undefined) {
      newRow["entity.name"] = `Copy ${originalName}`
    }
    if (newRow["id"] !== undefined) {
      newRow["id"] = `copy_${originalId}_${Date.now()}`
    }
    if (newRow["entity.id"] !== undefined) {
      newRow["entity.id"] = `copy_${originalId}_${Date.now()}`
    }

    const updatedData = [...rowData]
    updatedData.splice(selectedRowData.rowIndex + 1, 0, newRow)
    setRowData(updatedData)
    setIsComponentVisible(false)
    checkValidation(updatedData)
  }

  const handleAddRow = () => {
    const updatedData = [...rowData]
    const sampleRow = rowData[0] || {}
    const newItem = Object.keys(sampleRow).reduce((acc, key) => {
      if (key === "name" || key === "entity.name") {
        acc[key] = "New Entity"
      } else if (key === "id" || key === "entity.id") {
        acc[key] = `new_${Date.now()}`
      } else {
        acc[key] = ""
      }
      return acc
    }, {})

    updatedData.splice(selectedRowData.rowIndex + 1, 0, newItem)
    setRowData(updatedData)
    setIsComponentVisible(false)
    checkValidation(updatedData)
  }

  const handleDeleteRow = () => {
    if (deleteIds.length > 0) {
      const indexesSet = new Set(deleteIds)
      const updatedData = rowData.filter((_, index) => !indexesSet.has(index))
      setRowData(updatedData)
      setIsComponentVisible(false)
      checkValidation(updatedData)
      setDeleteIds([])
      return
    }

    const updatedData = [...rowData]
    updatedData.splice(selectedRowData.rowIndex, 1)
    setRowData(updatedData)
    setIsComponentVisible(false)
    checkValidation(updatedData)
  }

  const handleOnRowSelected = (event) => {
    const selectedNodes = event.api.getSelectedNodes()
    const indexes = selectedNodes.map((node) => node.rowIndex)
    setDeleteIds(indexes)
  }

  const onCellValueChanged = (event) => {
    const { rowIndex, colDef, newValue } = event

    setRowData((prevData) => {
      const updatedData = [...prevData]
      updatedData[rowIndex] = {
        ...updatedData[rowIndex],
        [colDef.field]: newValue,
      }
      return updatedData
    })

    setTimeout(() => {
      checkValidation(rowData)
    }, 100)
  }

  const onCellEditingStopped = (event) => {
    setError("")
    checkCellValidation(event.rowIndex, event.column.colId, event.value)
  }

  const onSortChanged = (params) => {
    const sortedData = []
    params.api.forEachNodeAfterFilterAndSort((node) => {
      sortedData.push(node.data)
    })
    checkValidation(sortedData)
    setRowData(sortedData)
  }

  const onGridReady = (params) => {
    params.api.sizeColumnsToFit()
  }

  const onCellClicked = (params) => {
    const { node, colDef } = params
    const rowIndex = node.rowIndex
    const field = colDef.field

    if (errors[rowIndex]?.[field]) {
      setError(`Error: ${errors[rowIndex][field]}`)
    } else {
      setError("")
    }
  }

  const columnDefs = useMemo(() => {
    if (rowData.length > 0) {
      const columns = Object.keys(rowData[0]).map((key) => ({
        headerName: key.replace(/entity\./g, "").replace(/_/g, " "),
        field: key,
        sortable: true,
        filter: true,
        resizable: true,
        editable: true,
        suppressSizeToFit: true,
        suppressColumnVirtualisation: true,
        cellStyle: (params) => {
          const rowIndex = params.node.rowIndex
          const classes = { textAlign: "start" }
          if (errors[rowIndex]?.[key]) {
            classes.border = "0.8px solid red"
            classes.backgroundColor = "#ffe6e6"
          }
          return classes
        },
        valueGetter: (params) => {
          const value = params.data?.[key]
          return value || ""
        },
        valueSetter: (params) => {
          params.data[key] = params.newValue
          return true
        },
        cellRenderer: (params) => {
          const value = params.value || ""
          return <span>{value}</span>
        },
      }))

      // Add actions column
      columns.push({
        headerName: "",
        field: "actions",
        maxWidth: 50,
        pinned: "right",
        sortable: false,
        filter: false,
        resizable: false,
        suppressSizeToFit: true,
        editable: false,
        cellRenderer: (params) => {
          return (
            <div
              onClick={(e) => handleOpenThreeDotMenu(e, params)}
              className="flex justify-center items-center h-full w-full"
            >
              <EllipsisVertical size={40} color="black" className="cursor-pointer" />
            </div>
          )
        },
      })

      // Add status column
      columns.unshift({
        headerName: content?.MANAGE_RECOMMENDATION?.labels?.status || "Status",
        field: "status",
        maxWidth: 90,
        sortable: false,
        pinned: "left",
        filter: false,
        resizable: true,
        suppressSizeToFit: true,
        suppressColumnVirtualisation: true,
        headerClass: "center-header ag-align-center-header w-full",
        cellStyle: { textAlign: "center" },
        editable: false,
        cellRenderer: (params) => {
          const rowIndex = params.node.rowIndex
          const hasError = errors[rowIndex] && Object.keys(errors[rowIndex]).length > 0
          return (
            <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
              {hasError ? <XCircle color="red" size={24} /> : <CheckCircle color="green" size={24} />}
            </div>
          )
        },
      })

      // Add row number column
      columns.unshift({
        headerName: "#",
        field: "rowNumber",
        maxWidth: 45,
        pinned: "left",
        sortable: false,
        filter: false,
        resizable: false,
        suppressSizeToFit: true,
        suppressColumnVirtualisation: true,
        cellStyle: { textAlign: "center" },
        editable: false,
        valueGetter: (params) => params.node.rowIndex + 1,
      })

      // Add checkbox column
      columns.unshift({
        headerName: "",
        field: "checkbox",
        maxWidth: 35,
        pinned: "left",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        suppressMovable: true,
        resizable: false,
        suppressSizeToFit: true,
        suppressColumnVirtualisation: true,
        cellStyle: { textAlign: "center" },
        editable: false,
      })

      return columns
    }
    return []
  }, [rowData, errors, content])

  const mappingColumnDefs = useMemo(() => {
    return [
      {
        headerName: '',
        field: 'id',
        width: 50,
        cellRenderer: ({ data }) => (
          <input
            type="radio"
            name="mappingSelect"
            checked={selectedMapping?.id === data.id}
            onChange={() => handleMappingSelect(data)}
          />
        ),
      },
      {
        headerName: content?.MANAGE_RECOMMENDATION?.mapping_columns?.name,
        field: 'name',
        flex: 1,
      },
      {
        headerName: content?.MANAGE_RECOMMENDATION?.mapping_columns?.action,
        field: 'action',
        width: 140,
        cellRenderer: ({ data }) => (
          <button
            className="text-primary hover:underline"
            onClick={() => {
              setEditingMapping(data);
              setIsMappingModalOpen(true);
            }}
          >
            {content?.MANAGE_RECOMMENDATION?.mapping_columns?.view_details}
          </button>
        ),
      }
    ];
  }, [mappings, selectedMapping]);

  const autoSizeStrategy = useMemo(() => {
    return {
      type: "fitCellContents",
    }
  }, [rowData])

  const handleFetchDataFromUrl = async (id) => {
    if (!url?.trim()) {
      toast.warn("Please enter URL first");
      return;
    }

    setLoading(true);
    setError("");
    setCsvValidation({ error: "", success: "" });
    setJsonData(null);
    setShowTargetPathInput(false);

    try {
      const response = await getDataFromCsvURL(url);
      const rawData = response?.data;

      if (id === ENTITY_TYPES.CSV_URL) {
        // Strict JSON handling
        try {
          const parsed = JSON.parse(rawData);
          setJsonData(parsed);
          setShowTargetPathInput(true);
          setCsvValidation({
            error: "",
            success: "JSON data fetched successfully. Please enter the target entity path.",
          });
        } catch {
          throw new Error("Invalid JSON format");
        }

      } else if (id === ENTITY_TYPES.XML_URL) {
        // Strict XML handling
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(rawData, "application/xml");
        const errorNode = xmlDoc.querySelector("parsererror");

        if (errorNode) {
          throw new Error("XML parsing error: " + errorNode.textContent);
        }

        const parsed = xmlToJsonSimplified(xmlDoc.documentElement);
        setJsonData(parsed);
        setShowTargetPathInput(true);
        setCsvValidation({
          error: "",
          success: "XML data fetched and parsed successfully. Please enter the target entity path.",
        });

      } else {
        toast.warn("Unsupported data source type.");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(
        id === "json-url"
          ? "Unable to fetch or parse JSON from this URL. Please enter a correct URL."
          : "Unable to fetch or parse XML from this URL. Please enter a correct URL."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleProcessTargetPath = async () => {
    if (!targetPath.trim()) {
      setError("Please enter a target entity path")
      return
    }

     setAvailableFields([])
     setStringFields([])
     setRowData([])
     setSelectedMapping(null)

    try {
      // Recursive function to extract data from nested arrays
      const extractDataRecursively = (data, pathParts, currentIndex = 0) => {
        // If we've processed all path parts, return the current data
        if (currentIndex >= pathParts.length) {
          return Array.isArray(data) ? data : [data]
        }

        const currentPart = pathParts[currentIndex]
        let nextData = data[currentPart]
        if (nextData === undefined) {
          nextData = []
        }

        // If this is an array, recursively process each item
        if (Array.isArray(nextData)) {
          const results = []
          nextData.forEach((item) => {
            const extracted = extractDataRecursively(item, pathParts, currentIndex + 1)
            results.push(...extracted)
          })
          return results
        } else {
          // If not an array, continue navigating
          return extractDataRecursively(nextData, pathParts, currentIndex + 1)
        }
      }

      // Split the path by dots and start recursive extraction
      const pathParts = targetPath.split(".")
      const extractedData = extractDataRecursively(jsonData, pathParts)

      if (extractedData.length === 0) {
        setAvailableFields([])
        throw new Error("No data found at the specified path")
      }

      // Set the processed data and extract string fields
      setError("")
      console.log("available fields", extractedData)
      setAvailableFields(extractedData)
      const fields = extractStringFields(extractedData)
      console.log("string fields", fields)
      setStringFields(fields)
      setShowFieldMappingSection(true)
      await fetchMappings()
      setCsvValidation({
        error: "",
        success: `Successfully extracted ${extractedData.length} entities from path "${targetPath}". Please map the fields to entity properties.`,
      })
    } catch (error) {
      console.error("Error processing target path:", error)
      setError(error.message || "Invalid target entity path")
      setShowFieldMappingSection(false)
    }
  }

  return (
    <>
      <PrimaryModal title={"Add Entity"} hideModal={handleCloseAddEntity} widthClass="max-w-6xl">
        {loading && <Loader />}
        {validateCsvLoading && (
          <div className="p-6 rounded-lg absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            <SpinLoader />
          </div>
        )}
        <div className="w-full p-6 space-y-4">
          <div className="flex gap-3 !mb-6">
            <p className="flex items-center text-black">
              {content?.MANAGE_RECOMMENDATION?.labels?.select_environment || "Select Environment"}
            </p>
            <Dropdown
              width="320"
              isWidthInPercentage={false}
              items={environments}
              callback={handleChooseEnvironment}
            />
            <p className="flex items-center text-black">
              {content?.MANAGE_RECOMMENDATION?.labels?.select_entity || "Select Entity"}
            </p>
            <Dropdown width="320" isWidthInPercentage={false} items={entityOptions} callback={handleChooseEntity} />
          </div>

          <>
            {selectedEntity?.id === ENTITY_TYPES.CSV_UPLOAD && <a id="download-sample-csv-btn-id" className="text-primary cursor-pointer m-0 p-0" onClick={downloadCsv}>
              {content?.MANAGE_RECOMMENDATION?.actions?.download_sample || "Download Sample File"}
            </a>}
            {(selectedEntity?.id === ENTITY_TYPES.CSV_UPLOAD ||
              selectedEntity?.id === ENTITY_TYPES.CSV_UPLOAD_WITH_CUSTOM_TEMPLATE) && <div>
                <input
                  disabled={loading || validateCsvLoading || !!!selectedEntity}
                  id="upload-csv-file-id"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleSelectCSV}
                  accept=".csv"
                />
              </div>}
          </>

          <div className="space-y-4">
            {/* URL Input */}
            {(selectedEntity?.id === ENTITY_TYPES.CSV_URL || selectedEntity?.id === ENTITY_TYPES.XML_URL) && <div className="flex flex-col space-y-2">
              <label htmlFor="csv-url-input" className="text-sm font-medium">
                {selectedEntity?.id === ENTITY_TYPES.CSV_URL ? content?.MANAGE_RECOMMENDATION?.labels?.json_url : content?.MANAGE_RECOMMENDATION?.labels?.xml_url}
              </label>
              <div className="flex gap-2">
                <input
                  id="csv-url-input"
                  type="text"
                  placeholder={ selectedEntity?.id === ENTITY_TYPES.CSV_URL ? content?.MANAGE_RECOMMENDATION?.labels?.json_url_placeholder : content?.MANAGE_RECOMMENDATION?.labels?.xml_url_placeholder}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-2"
                  disabled={loading}
                />
                <button
                  onClick={() => handleFetchDataFromUrl(selectedEntity?.id)}
                  disabled={loading || !url.trim()}
                  className="bg-primary hover:bg-primary-2 text-white px-4 py-2 rounded disabled:bg-primary"
                >
                  {content?.MANAGE_RECOMMENDATION?.actions?.fetch_data}
                </button>
              </div>
            </div>}

            {/* Target Path Input (shown after URL fetch) */}
            {(selectedEntity?.id === ENTITY_TYPES.CSV_URL || selectedEntity?.id === ENTITY_TYPES.XML_URL) && showTargetPathInput && (
              <div className="flex flex-col space-y-2">
                <label htmlFor="target-path-input" className="text-sm font-medium">
                  {content?.MANAGE_RECOMMENDATION?.labels?.target_path}
                </label>
                <div className="flex gap-2">
                  <input
                    id="target-path-input"
                    type="text"
                    placeholder="products"
                    value={targetPath}
                    onChange={(e) => setTargetPath(e.target.value)}
                    className="flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-2"
                    disabled={loading}
                  />
                  <button
                    onClick={handleProcessTargetPath}
                    disabled={loading || !targetPath.trim()}
                    className="bg-primary hover:bg-primary-2 text-white px-4 py-2 rounded disabled:bg-primary"
                  >
                    {content?.MANAGE_RECOMMENDATION?.actions?.process_path}
                  </button>
                </div>
              </div>
            )}

            {/* Field Mapping (shown after target path validation) */}
            {(selectedEntity?.id === ENTITY_TYPES.CSV_URL || selectedEntity?.id === ENTITY_TYPES.XML_URL ||
              selectedEntity?.id === ENTITY_TYPES.CSV_UPLOAD_WITH_CUSTOM_TEMPLATE) &&
              showFieldMappingSection &&
              stringFields.length > 0 && (
                <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium">
                      {content?.MANAGE_RECOMMENDATION?.heading?.map_fields}
                    </h3>

                    <div className="text-right">
                      <PrimaryButton
                        handleClick={() => {
                          setEditingMapping(null);
                          setIsMappingModalOpen(true);
                        }}
                      >
                        {content?.MANAGE_RECOMMENDATION?.actions?.create_mapping}
                      </PrimaryButton>
                    </div>
                  </div>

                  {/* Grid with auto layout */}
                  <div className="ag-theme-quartz mb-4 w-full">
                    <AgGridWrapper
                      rowData={mappings}
                      columnDefs={mappingColumnDefs}
                      autoSizeStrategy={{ type: 'fitGridWidth' }}
                      rowSelection="single"
                      domLayout="autoHeight"
                    />
                  </div>

                  {/* Add a dummy element for spacing */}
                  <div className="h-4" />

                  {isMappingModalOpen && (
                    <MappingModal
                      onClose={() => {
                        setIsMappingModalOpen(false);
                        setEditingMapping(null);
                      }}
                      onSave={handleCreateMapping}
                      fields={stringFields}
                      loading={loading}
                      defaultMapping={editingMapping}
                    />
                  )}
                </div>
              )}
          </div>

          <div className="space-y-2 mt-4 h-[48px]">
            {!error && !csvValidation.error && (
              <div className="flex items-center h-full">
                <p className="text-sm text-[#27942B]">{csvValidation.success}</p>
              </div>
            )}
            <div className="flex flex-col">
              <p className="text-sm text-red-500">{csvValidation.error}</p>
              <p className="text-sm text-red-500">{error}</p>
            </div>
          </div>

          {rowData.length > 0 && (
            <div className={"ag-theme-quartz"} style={{ height: "100%", width: "100%" }}>
              <div className="ag-theme-quartz z-50" style={{ width: "100%" }}>
                <AgGridWrapper
                  rowData={rowData}
                  columnDefs={columnDefs}
                  autoSizeStrategy={autoSizeStrategy}
                  rowSelection="multiple"
                  suppressRowClickSelection
                  onRowSelected={handleOnRowSelected}
                  onGridReady={onGridReady}
                  onSortChanged={onSortChanged}
                  onCellValueChanged={onCellValueChanged}
                  onCellClicked={onCellClicked}
                  onCellEditingStopped={onCellEditingStopped}
                  stopEditingWhenCellsLoseFocus={true}
                />
              </div>

              <div
                ref={ref}
                className={`absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${isComponentVisible ? "" : "hidden"}`}
                style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
              >
                <div className="py-1">
                  <a
                    id="add-duplicate-of-first-row-btn-id"
                    onClick={handleAddDuplicateRow}
                    href="#"
                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                    tabIndex="-1"
                  >
                    {content?.MANAGE_RECOMMENDATION?.actions?.duplicate_row || "Duplicate Row"}
                  </a>
                  <a
                    onClick={handleAddRow}
                    href="#"
                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                    tabIndex="-1"
                  >
                    {content?.MANAGE_RECOMMENDATION?.actions?.add_new_row || "Add New Row"}
                  </a>
                  <a
                    onClick={handleDeleteRow}
                    href="#"
                    className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                    tabIndex="-1"
                  >
                    {content?.MANAGE_RECOMMENDATION?.actions?.delete_row || "Delete Row"}
                  </a>
                </div>
              </div>

              <div className="mt-2">
                <p className="text-sm italic">{content.MANAGE_RECOMMENDATION.notes[0]}</p>
                <p className="text-sm italic">{content.MANAGE_RECOMMENDATION.notes[1]}</p>
              </div>
            </div>
          )}
          <div className="flex justify-between items-center ">
            <div className="flex items-center h-full self-end py-2 pb-4">
              <p className={`text-sm ${saveEntityMessage.type === "success" ? "text-[#27942B]" : "text-red-500"}`}>{saveEntityMessage.text}</p>
            </div>
            <div className="flex items-center justify-end space-x-4 sticky bottom-0 bg-white px-6 py-2 pb-4">
              <div className="space-x-4">
                {deleteIds.length > 0 && (
                  <ErrorButton handleClick={() => handleDeleteRow(deleteIds)}>
                    {content?.MANAGE_RECOMMENDATION?.actions?.delete_selected || "Delete Selected"}
                  </ErrorButton>
                )}
                <PrimaryButton
                  id={"submit-btn-id"}
                  disable={
                    loading || validateCsvLoading || !!csvValidation.error || !selectedEnvironment || rowData.length === 0
                  }
                  handleClick={handleSubmit}
                >
                  {content?.MANAGE_RECOMMENDATION?.actions?.save_entities || "Save Entities"}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      </PrimaryModal>
    </>
  )
}

export default ManageRecommendationAddEntity